/**
 * Un mouvement qui en annule un autre : rembourser SANS note de crédit.
 *
 * ## Le cas
 *
 * Signalé le 20 septembre 2026, facture `CDV-WBK-2601307` (Claire Musolino) :
 *
 * > « La maman a demandé à se faire rembourser les 49 € car un service tiers
 * > (SPJ) allait payer cette facture. Le service n'a toujours pas payé mais
 * > nous l'avons remboursée. J'ai fait sortir le montant de son compte mais la
 * > facture apparaît toujours en payée. Je devrais avoir le choix de la facture
 * > que cette sortie modifie : elle a été payée mais a été remboursée — pas de
 * > note de crédit dans ce cas, mais **un mouvement qui en annule un autre**. »
 *
 * ## En partie double
 *
 * Le grand livre de la famille porte quatre natures : facture (+), note de
 * crédit (−), encaissement (−), remboursement (+). Les remboursements connus
 * jusqu'ici s'adossaient à une note de crédit : la note (−) et le
 * remboursement (+) s'annulent, la créance a bien été effacée, et l'argent est
 * reparti. **Rien n'est dû.**
 *
 * Ici, il n'y a pas de note. La facture reste entière et l'encaissement (−) est
 * repris par le remboursement (+). Le solde de la famille redevient celui
 * d'avant le paiement : **la facture est de nouveau due**. C'est exact — le SPJ
 * n'a pas payé, la créance existe toujours, elle a simplement changé de
 * débiteur attendu. Une note de crédit dirait le contraire : qu'on renonce à
 * réclamer les 49 €.
 *
 * ## Ce que l'imputation à la provision faisait à la place
 *
 * Mesuré sur ce dossier même, avant que ce chemin existe : les 49 € ont été
 * imputés à `provision` faute de mieux. La famille n'avait aucune provision, et
 * `v_user_provision_balances.avoir_cents` vaut `GREATEST(0, versé − rendu)` —
 * les 49 € rendus sur une provision inexistante se sont donc évanouis dans le
 * plancher, pendant que la facture restait `paid` avec 0,00 € de reste dû.
 * Le grand livre de la famille, lui, était juste. C'est l'échelon de la pièce
 * qui mentait, et c'est celui qu'on relance.
 *
 * ## Ce module ne refuse jamais
 *
 * Même règle que partout dans le sortant, posée le 19 septembre : le virement
 * est parti en banque. On peut imputer plus que ce que la facture a encaissé,
 * ou sur une facture qui n'a rien encaissé — l'écran le DIT, et laisse décider.
 */

/** Une facture citée par un virement sortant, et ce qu'elle a encaissé. */
export interface EncaissementDeFacture {
  invoiceId: string;
  invoiceNumber: string;
  userId: string | null;
  famille: string | null;
  /** Le montant facturé, avant notes de crédit. */
  montantCents: number;
  /** Ce qu'elle réclame réellement : montant moins notes de crédit. */
  netCents: number;
  /** Ce que ses notes de crédit valent. Zéro dans le cas qui a fait ce module. */
  notesCents: number;
  /** Ce qui a été encaissé dessus, avant toute annulation. */
  encaisseCents: number;
  /** Ce qu'un remboursement a DÉJÀ annulé dessus. */
  dejaAnnuleCents: number;
  /** `paid`, `partial`, `unpaid`, `credited`, `cancelled`. */
  reglement: string | null;
}

/** Comment le virement et l'encaissement de la facture se répondent. */
export type AccordDeLAnnulation =
  /** Le virement reprend exactement ce que la facture a encaissé. */
  | 'exact'
  /** Il n'en reprend qu'une partie ; la facture reste payée pour le reste. */
  | 'partielle'
  /** Le virement vaut plus que ce que la facture a encaissé. */
  | 'excedentaire'
  /** Cette facture n'a rien encaissé, ou tout a déjà été repris. */
  | 'rien';

export interface AnnulationDEncaissement {
  facture: EncaissementDeFacture;
  /** Ce qui reste annulable : encaissé moins déjà annulé. */
  annulableCents: number;
  /** Ce que le bouton écrira. */
  proposeCents: number;
  accord: AccordDeLAnnulation;
  /** Ce que la facture redeviendra due si on écrit. */
  redeviendraDuCents: number;
  /** L'état, en une phrase qu'un écran affiche sans rien y ajouter. */
  phrase: string;
  /** Vrai quand la situation mérite d'être regardée avant d'écrire. */
  aVerifier: boolean;
  /**
   * Vrai quand la facture porte une note de crédit. Le remboursement se
   * rattache alors normalement à la NOTE, pas à l'encaissement : ce chemin-ci
   * est celui des factures sans note.
   */
  uneNoteExiste: boolean;
  /**
   * Vrai quand la provision de la famille couvre le virement. C'est alors elle
   * qu'il faut rendre, et non l'encaissement qu'il faut annuler. Voir
   * `ContexteDeLaFamille.provisionCents`.
   */
  laProvisionExplique: boolean;
}

/** Ce que l'on sait par ailleurs de la famille, et qui change la lecture. */
export interface ContexteDeLaFamille {
  /**
   * Ce que la famille a en provision.
   *
   * ## Pourquoi il entre ici
   *
   * Vu le 20 septembre 2026 sur le virement de 180,00 € à Sarah Lacour, qui
   * cite `CDV-250602-00070` : les deux blocs de l'écran s'affichaient en vert.
   * « Rendre 180,00 € de provision » et « Annuler 180,00 € encaissés sur
   * CDV-250602-00070 » se proposaient avec la même assurance, et un seul des
   * deux est juste.
   *
   * La distinction est celle du **double paiement**. La famille a payé cette
   * facture DEUX fois : une fois imputée à la facture, une fois versée en
   * provision. Le virement rend le second versement. La facture, elle, a bel et
   * bien été payée, et l'annuler dirait le contraire — elle redeviendrait due
   * de 180,00 € alors que l'argent est chez nous.
   *
   * Une provision qui couvre le virement est donc la marque d'un double
   * paiement, et elle DÉCLASSE l'annulation. Elle ne l'interdit pas : le
   * rapprochement des entrées n'est pas terminé, et une provision peut aussi
   * venir d'ailleurs.
   */
  provisionCents?: number;
}

export function lireLAnnulation(
  aPlacerCents: number,
  facture: EncaissementDeFacture,
  contexte: ContexteDeLaFamille = {},
): AnnulationDEncaissement {
  const provision = Math.max(0, contexte.provisionCents ?? 0);
  const annulable = Math.max(0, facture.encaisseCents - facture.dejaAnnuleCents);

  /*
    Quand la facture a encaissé, on ne propose jamais plus que ce qu'elle a
    reçu : le reste du virement n'annule aucun encaissement d'ici, et le
    laisser à placer est plus juste que de l'y fondre. Quand elle n'a rien
    encaissé, il n'y a rien de mieux à proposer que le montant entier —
    accompagné de la phrase qui dit que rien ne l'appuie.
  */
  const propose = annulable > 0 ? Math.min(aPlacerCents, annulable) : aPlacerCents;

  let accord: AccordDeLAnnulation;
  if (annulable === 0) accord = 'rien';
  else if (annulable === aPlacerCents) accord = 'exact';
  else if (annulable > aPlacerCents) accord = 'partielle';
  else accord = 'excedentaire';

  /* La même arithmétique que `v_invoice_balances` : reste dû = net moins ce
     qui reste encaissé, et ce qui reste encaissé ne descend pas sous zéro. */
  const encaisseApres = Math.max(0, facture.encaisseCents - facture.dejaAnnuleCents - propose);
  const redeviendraDu = Math.max(0, facture.netCents - encaisseApres);

  /* Une provision qui couvre le virement explique tout sans rendre la facture
     due : le double paiement. Voir `ContexteDeLaFamille.provisionCents`. */
  const laProvisionExplique = provision > 0 && provision >= aPlacerCents;

  return {
    facture,
    annulableCents: annulable,
    proposeCents: propose,
    accord,
    redeviendraDuCents: redeviendraDu,
    phrase: phrase(aPlacerCents, annulable, redeviendraDu, accord, facture)
      + (laProvisionExplique
        ? ` Attention : cette famille a ${euros(provision)} en provision, et c'est la `
          + `lecture la plus probable — un double paiement se rend par la provision, `
          + `et ${num(facture)} a bel et bien été payée.`
        : ''),
    aVerifier: accord === 'rien' || accord === 'excedentaire'
      || facture.notesCents > 0 || laProvisionExplique,
    uneNoteExiste: facture.notesCents > 0,
    laProvisionExplique,
  };
}

function num(f: EncaissementDeFacture): string {
  return f.invoiceNumber;
}

function phrase(
  aPlacerCents: number,
  annulable: number,
  redeviendraDu: number,
  accord: AccordDeLAnnulation,
  facture: EncaissementDeFacture,
): string {
  const num = facture.invoiceNumber;
  const note = facture.notesCents > 0
    ? ` Attention : cette facture porte ${euros(facture.notesCents)} de notes de crédit — `
      + `un remboursement se rattache normalement à la note, pas à l'encaissement.`
    : '';

  switch (accord) {
    case 'exact':
      return `${num} a encaissé ${euros(annulable)}, et ce virement les reprend exactement. `
        + `La facture redeviendra due de ${euros(redeviendraDu)}.${note}`;
    case 'partielle':
      return `${num} a encaissé ${euros(annulable)}. Ce virement en reprend ${euros(aPlacerCents)} ; `
        + `il restera ${euros(annulable - aPlacerCents)} d'encaissé, et ${euros(redeviendraDu)} redeviendront dus.${note}`;
    case 'excedentaire':
      return `${num} n'a encaissé que ${euros(annulable)}, et ce virement vaut ${euros(aPlacerCents)}. `
        + `Les ${euros(aPlacerCents - annulable)} de différence n'annulent aucun encaissement de cette facture : à vérifier.${note}`;
    case 'rien':
      return facture.encaisseCents === 0
        ? `${num} n'a jamais rien encaissé : il n'y a aucun encaissement à annuler ici. `
          + `Le virement est pourtant parti en banque — à vérifier.${note}`
        : `${num} a encaissé ${euros(facture.encaisseCents)}, mais ${euros(facture.dejaAnnuleCents)} `
          + `ont déjà été repris par un remboursement : il ne reste rien à annuler. À vérifier.${note}`;
  }
}

/** Les factures citées qu'un virement peut « dé-payer », la plus encaissée d'abord. */
export function annulationsPossibles(
  aPlacerCents: number,
  factures: EncaissementDeFacture[],
  /** Ce que chaque famille a en provision, par `userId`. Voir `lireLAnnulation`. */
  provisions: Map<string, number> = new Map(),
): AnnulationDEncaissement[] {
  const parFacture = new Map<string, EncaissementDeFacture>();
  for (const f of factures) if (!parFacture.has(f.invoiceId)) parFacture.set(f.invoiceId, f);

  return [...parFacture.values()]
    .map((f) => lireLAnnulation(aPlacerCents, f, {
      provisionCents: f.userId ? provisions.get(f.userId) : undefined,
    }))
    /* Celle dont l'encaissement explique le mieux ce virement, en tête. */
    .sort((a, b) => b.annulableCents - a.annulableCents);
}

const EUR = new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' });
function euros(cents: number): string {
  return EUR.format(cents / 100);
}
